/**
 * The one mutation path of the run page: names the in-flight action,
 * reloads on success, and turns a failure into a sentence — or a refresh,
 * when the server has already advanced the run past the action.
 */

import { useCallback, useState } from 'react';

import { ApiFailure } from '../../api.js';

export type RunMutation = (
  name: string,
  action: () => Promise<unknown>,
  refreshesStaleState?: (failure: unknown) => boolean,
) => Promise<void>;

export function useAnalysisRunMutation(reload: () => void): {
  readonly busy: string | null;
  readonly error: string | null;
  readonly mutate: RunMutation;
} {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      name: string,
      action: () => Promise<unknown>,
      refreshesStaleState?: (failure: unknown) => boolean,
    ) => {
      setBusy(name);
      setError(null);
      try {
        await action();
        reload();
      } catch (failure) {
        // Merchant automation may advance the durable run after this render but
        // before an Owner action reaches the server. In that race the server is
        // authoritative: refresh the new stage instead of presenting a false
        // collection failure for work that is already running or complete.
        if (refreshesStaleState?.(failure) === true) {
          reload();
          return;
        }
        setError(
          failure instanceof ApiFailure
            ? (failure.detail ?? failure.message)
            : 'The analysis operation could not be completed.',
        );
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  return { busy, error, mutate };
}
